import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BegeleidingOverzicht, NieuweGecoachte, Seniority } from '@vip/shared';
import { api } from '../../lib/apiClient';

const begeleidingKeys = { alle: ['begeleiding'] as const };

export function useBegeleiding() {
  return useQuery({
    queryKey: begeleidingKeys.alle,
    queryFn: () => api.get<BegeleidingOverzicht>('/begeleiding'),
  });
}

/** Elke mutatie hier ververst ook `['deelnemers']`: dat voedt de collegakiezer
 *  van "Nieuw gesprek", en zonder deze tweede sleutel verschijnt een net
 *  aangemaakte of gekoppelde persoon daar pas na een handmatige verversing. */
function useBegeleidingMutatie<TInvoer, TData = unknown>(fn: (invoer: TInvoer) => Promise<TData>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: begeleidingKeys.alle });
      qc.invalidateQueries({ queryKey: ['deelnemers'] });
    },
  });
}

export const useMaakPersoon = () =>
  useBegeleidingMutatie((invoer: NieuweGecoachte) =>
    api.post<{ id: string }>('/begeleiding/personen', invoer),
  );

export const useKoppelAanMij = () =>
  useBegeleidingMutatie((medewerkerId: string) =>
    api.post('/begeleiding/koppelingen', { medewerkerId }),
  );

export const useMaakOpdracht = () =>
  useBegeleidingMutatie((invoer: { opdrachtgever: string; opdracht: string }) =>
    api.post<{ opdrachtId: string }>('/begeleiding/opdrachten', invoer),
  );

export const useZetOpdracht = () =>
  useBegeleidingMutatie(({ id, opdrachtIds }: { id: string; opdrachtIds: string[] }) =>
    api.patch(`/begeleiding/gecoachten/${id}/opdracht`, { opdrachtIds }),
  );

export const useZetNiveau = () =>
  useBegeleidingMutatie(({ id, niveau }: { id: string; niveau: Seniority }) =>
    api.patch(`/begeleiding/gecoachten/${id}/niveau`, { niveau }),
  );
